import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../../lib/supabaseClient'
import { useAuth } from '../../context/AuthContext'

const MIN_PASSWORD_LENGTH = 6
const MAX_PASSWORD_LENGTH = 72

const AuthPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { refreshProfile } = useAuth()

  // Si on vient d’une autre page, on y retournera après login
  const from = (location.state as any)?.from?.pathname || '/order/start'

  // Onglet actif : 'login' ou 'register'
  const [mode, setMode] = useState<'login' | 'register'>('login')

  // Champs communs
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Champs inscription
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  
  const isPasswordLongEnough =
    password.length >= MIN_PASSWORD_LENGTH && password.length <= MAX_PASSWORD_LENGTH

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setInfo(null)
    setLoading(true)

    const { data: authData, error } = await supabase.auth.signInWithPassword({ email, password })

    if (error) {
      if (error.message.toLowerCase().includes('email not confirmed')) {
        setError(
          "Ton adresse email n'est pas encore confirmée. Vérifie tes emails et clique sur le lien de confirmation."
        )
      } else {
        setError('Identifiants incorrects ou compte inexistant.')
      }
      setLoading(false)
      return
    }

    try {
      await refreshProfile()
    } catch (err) {
      console.error("Erreur refreshProfile:", err)
    }

    // Vérifier si le profil est complet (nom complet + téléphone requis)
    let redirectTarget = from
    const userId = authData?.user?.id
    if (userId) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name, phone')
        .eq('id', userId)
        .single()

      const isIncomplete = !profileData?.full_name?.trim() || !profileData?.phone?.trim()
      if (isIncomplete) {
        redirectTarget = `/account/personal-info?redirect=${encodeURIComponent(from)}`
      }
    }

    setLoading(false)
    navigate(redirectTarget, { replace: true })
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setInfo(null)
    setLoading(true)

    if (!name || !email || !password) {
      setError('Merci de renseigner tous les champs obligatoires (nom, email, mot de passe).')
      setLoading(false)
      return
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`)
      setLoading(false)
      return
    }

    if (password.length > MAX_PASSWORD_LENGTH) {
      setError(`Le mot de passe doit contenir au maximum ${MAX_PASSWORD_LENGTH} caractères.`)
      setLoading(false)
      return
    }

    // Inscription Supabase : envoi d’un email de confirmation (si activé dans le projet)
    const { data, error: signupError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // Supabase enverra un email avec un lien qui connectera automatiquement l’utilisateur
        // et le redirigera vers la SITE_URL configurée dans Supabase.
        data: {
          full_name: name,
          ...(phone.trim() ? { phone: phone.trim() } : {}),
        },
      },
    })

    if (signupError || !data.user) {
      console.error('Erreur signUp:', signupError)
      setError("Impossible de créer le compte. Vérifie tes informations.")
      setLoading(false)
      return
    }

    setLoading(false)

    // On n’essaie PAS de connecter l’utilisateur maintenant si la confirmation email est requise.
    // On lui explique clairement la suite du process.
    setMode('login')
    setInfo(
      `Ton compte a été créé. Un email de confirmation vient d'être envoyé à ${email}. 
Clique sur le lien dans cet email pour activer ton compte et te connecter automatiquement.`
    )
  }

  return (
    <div
      style={{
        maxWidth: '480px',
        margin: '2rem auto',
        background: '#0f172a',
        color: '#f1f5f9',
        borderRadius: '1rem',
        padding: '2rem',
        boxShadow: '0 0 20px rgba(0,0,0,0.4)',
      }}
    >
      <h1 style={{ textAlign: 'center', fontSize: '1.4rem', marginBottom: '1rem' }}>
        {mode === 'login' ? 'Connexion' : 'Créer un compte'}
      </h1>

      {/* Tabs */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
        <button
          data-testid="tab-auth-mode-login"
          className={`btn ${mode === 'login' ? 'btn-primary' : 'btn-ghost'}`}
          style={{ width: '50%' }}
          onClick={() => {
            setMode('login')
            setError(null)
            setInfo(null)
          }}
        >
          Se connecter
        </button>
        <button
          data-testid="tab-auth-mode-register"
          className={`btn ${mode === 'register' ? 'btn-primary' : 'btn-ghost'}`}
          style={{ width: '50%' }}
          onClick={() => {
            setMode('register')
            setError(null)
            setInfo(null)
          }}
        >
          S’inscrire
        </button>
      </div>

      {error && (
        <p style={{ color: '#f87171', marginBottom: '1rem', textAlign: 'center' }}>{error}</p>
      )}

      {info && (
        <p style={{ color: '#22c55e', marginBottom: '1rem', textAlign: 'center', whiteSpace: 'pre-line' }}>
          {info}
        </p>
      )}

      {/* Formulaire connexion */}
      {mode === 'login' && (
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '1rem' }}>
            <label className="label">Email</label>
            <input
              data-testid="auth-login-email"
              className="input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label className="label">Mot de passe</label>
            <input
              data-testid="auth-login-password"
              className="input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>
          <button data-testid="btn-auth-login-submit" className="btn btn-primary" type="submit" disabled={loading}>
            {loading ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>
      )}

      {/* Formulaire inscription */}
      {mode === 'register' && (
        <form onSubmit={handleRegister}>
          <div style={{ marginBottom: '1rem' }}>
            <label className="label">Nom complet</label>
            <input
              data-testid="auth-register-name"
              className="input"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label className="label">Email</label>
            <input
              data-testid="auth-register-email"
              className="input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label className="label">Téléphone (optionnel, mais utile en cas de contact pour la prise en charge)</label>
            <input
              data-testid="auth-register-phone"
              className="input"
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="+41 XX XXX XX XX"
            />
          </div>

          <div style={{ marginBottom: '0.5rem' }}>
            <label className="label">Mot de passe</label>
            <input
              data-testid="auth-register-password"
              className="input"
              type="password"
              value={password}
              minLength={MIN_PASSWORD_LENGTH}
              maxLength={MAX_PASSWORD_LENGTH}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>

          {/* Aide sur les contraintes du mot de passe */}
          <div style={{ fontSize: '.8rem', color: '#9ca3af', marginBottom: '1rem' }}>
            <div>
              Longueur :{' '}
              <span style={{ color: isPasswordLongEnough ? '#22c55e' : '#f87171' }}>
                {password.length} / {MAX_PASSWORD_LENGTH} caractères
              </span>
            </div>
            <div>Le mot de passe doit contenir au moins {MIN_PASSWORD_LENGTH} caractères.</div>
            <div>Pour plus de sécurité, ajoute majuscules, minuscules, chiffres et caractères spéciaux.</div>
          </div>

          <button data-testid="btn-auth-register-submit" className="btn btn-primary" type="submit" disabled={loading}>
            {loading ? 'Création…' : 'Créer mon compte'}
          </button>
        </form>
      )}

      {/* Lien mot de passe oublié */}
      {mode === 'login' && (
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <button
            type="button"
            data-testid="auth-forgot-password"
            className="btn btn-ghost"
            onClick={() => navigate('/auth/forgot-password')}
          >
            Mot de passe oublié ?
          </button>
        </div>
      )}
    </div>
  )
}

export default AuthPage
